import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { SpouseInformation, HomeAddressData } from '../../../types/customer-portal';
import DatePicker from '../../../../../shared/components/ui/DatePicker/DatePicker';
import SearchableDropdown from '../../../../../components/common/SearchableDropdown';
import { dateUtils } from '../../../../../shared/utils/dateUtils';
import './SpouseInformationModal.css';
import '../../../styles/zipCodeAutoPopulation.css';

import { useIDTypes } from '@/portals/customer/hooks/useIDTypes';
import { useNationalities } from '@/portals/customer/hooks/useNationality';
import { useLocalAddressesProvinces } from '@/portals/customer/hooks/useLocalAddressesProvinces';
import { useLocalAddressesCities } from '@/portals/customer/hooks/useLocalAddressesCities';
import { useLocalAddressesBarangays } from '@/portals/customer/hooks/useLocalAddressesBarangays';
import { getProvinceCodeFromDescription } from '../../../utils/provinceMapper';
import { getZipCodeFromBarangay, shouldAutoPopulateZipCode } from '../../../utils/barangayZipCodeUtils';

// Import validation utilities
import {
  validateNameField,
  filterNameField,
  validateAddressField,
  filterAddressField,
  validateIdNumberField,
  filterIdNumberField,
  NAME_FIELD_MAX_LENGTH,
  ADDRESS_FIELD_MAX_LENGTH,
  ID_NUMBER_MAX_LENGTH
} from '../../../../../shared/utils/fieldValidation';


interface SpouseInformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (spouse: SpouseInformation) => void;
  onDelete?: (spouseId: string) => void;
  spouse?: SpouseInformation | null;
  isEditMode?: boolean;
  principalHomeAddress?: HomeAddressData | null; // NEW: Principal Insured home address
}

const SpouseInformationModal: React.FC<SpouseInformationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  spouse,
  isEditMode = false,
  principalHomeAddress
}) => {

  const [formData, setFormData] = useState<Omit<SpouseInformation, 'id'>>({
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: 'N/A',
    dateOfBirth: '',
    age: '',
    gender: '',
    nationality: '', // NEW: Added nationality field
    shareAddressWithChild: false, // Keep for backward compatibility
    shareAddressWithPrincipal: false, // NEW: Share address with Principal Insured
    houseUnitNo: '',
    streetName: '',
    subdivisionVillage: '',
    barangay: '',
    city: '',
    province: '',
    zipCode: '',
    country: 'Philippines',
    idType: '',
    idNumber: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [originalFormData, setOriginalFormData] = useState<Omit<SpouseInformation, 'id'> | null>(null);
  
  // Field validation highlighting state (like PrincipalInsuredStep)
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  // Zip code auto-population state
  const [isZipCodeAutoPopulated, setIsZipCodeAutoPopulated] = useState(false);

  // Use the custom hook to fetch valid IDs or ID Types
  const { idTypesOptions, loading: idTypesLoading, error: idTypesError } = useIDTypes();
  
  // Use the custom hook to fetch nationalities
  const { nationalitiesOptions, loading: nationalitiesLoading, error: nationalitiesError } = useNationalities();
  
  // Use the local addresses provinces hook
  const { provinces, provinceOptions, loading: provincesLoading, error: provincesError} = useLocalAddressesProvinces();
  
  // Get selected province code for city filtering
  const selectedProvinceCode = useMemo(() => {
    return getProvinceCodeFromDescription(provinces, formData.province || '');
  }, [provinces, formData.province]);

  // Use the local addresses cities hook with province filtering
  const { cityOptions, loading: citiesLoading, error: citiesError} = useLocalAddressesCities(selectedProvinceCode);
  
  // Use the local addresses barangays hook with city filtering
  const { barangays, barangayOptions, loading: barangaysLoading, error: barangaysError} = useLocalAddressesBarangays(formData.city);
  
  // Reset form when modal opens/closes or spouse changes
  useEffect(() => {
    if (isOpen) {
      if (spouse && isEditMode) {
        const initialData = {
          title: spouse.title,
          firstName: spouse.firstName,
          middleName: spouse.middleName || '',
          lastName: spouse.lastName,
          suffix: spouse.suffix,
          dateOfBirth: spouse.dateOfBirth,
          age: spouse.age,
          gender: spouse.gender,
          nationality: spouse.nationality || '', // NEW: Handle nationality field
          shareAddressWithChild: spouse.shareAddressWithChild,
          shareAddressWithPrincipal: spouse.shareAddressWithPrincipal || false, // NEW: Handle new field
          houseUnitNo: spouse.houseUnitNo,
          streetName: spouse.streetName,
          subdivisionVillage: spouse.subdivisionVillage,
          barangay: spouse.barangay,
          city: spouse.city,
          province: spouse.province,
          zipCode: spouse.zipCode,
          country: spouse.country,
          idType: spouse.idType,
          idNumber: spouse.idNumber
        };
        setFormData(initialData);
        setOriginalFormData(initialData);
        setHasFormChanges(false);
      } else {
        const initialData = {
          title: 'Ms.',
          firstName: '',
          middleName: '',
          lastName: '',
          suffix: 'N/A',
          dateOfBirth: '',
          age: '',
          gender: '',
          nationality: '', // NEW: Initialize nationality field
          shareAddressWithChild: false,
          shareAddressWithPrincipal: false, // NEW: Initialize new field
          houseUnitNo: '',
          streetName: '',
          subdivisionVillage: '',
          barangay: '',
          city: '',
          province: '',
          zipCode: '',
          country: 'Philippines',
          idType: '',
          idNumber: ''
        };
        setFormData(initialData);
        setOriginalFormData(null);
        setHasFormChanges(false);
      }
      setErrors({});
    }
  }, [isOpen, spouse, isEditMode]);

  // NEW: Handle Principal Insured address auto-population
  useEffect(() => {
    // Only update if modal is open and not in edit mode
    if (isOpen && !isEditMode && principalHomeAddress) {
      if (formData.shareAddressWithPrincipal === true) {
        console.log('SpouseInformationModal - Principal address sharing enabled, updating form data');
        // Combine houseUnitNo and streetName from home address for the address field
        const combinedAddress = [
          principalHomeAddress.houseUnitNo || '',
          principalHomeAddress.streetName || ''
        ].filter(Boolean).join(' ').trim();
        
        setFormData(prev => ({
          ...prev,
          houseUnitNo: combinedAddress || principalHomeAddress.houseUnitNo || '',
          streetName: principalHomeAddress.streetName || '',
          subdivisionVillage: principalHomeAddress.subdivisionVillage || '',
          barangay: principalHomeAddress.barangay || '',
          city: principalHomeAddress.city || '',
          province: principalHomeAddress.province || '',
          zipCode: principalHomeAddress.zipCode || '',
          country: principalHomeAddress.country || 'Philippines'
        }));
      } else if (formData.shareAddressWithPrincipal === false) {
        console.log('SpouseInformationModal - Principal address sharing disabled, clearing address fields');
        setFormData(prev => ({
          ...prev,
          houseUnitNo: '',
          streetName: '',
          subdivisionVillage: '',
          barangay: '',
          city: '',
          province: '',
          zipCode: '',
          country: 'Philippines'
        }));
        setIsZipCodeAutoPopulated(false);
      }
    }
  }, [
    formData.shareAddressWithPrincipal,
    isOpen,
    isEditMode,
    principalHomeAddress
  ]);

  // Calculate age when date of birth changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const age = dateUtils.calculateAge(new Date(formData.dateOfBirth));
      setFormData(prev => ({ ...prev, age: age.toString() }));
    }
  }, [formData.dateOfBirth]);

  // Detect form changes for save button state
  useEffect(() => {
    if (isEditMode && originalFormData) {
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setHasFormChanges(hasChanges);
    } else {
      setHasFormChanges(false);
    }
  }, [formData, originalFormData, isEditMode]);

  const handleInputChange = (field: keyof Omit<SpouseInformation, 'id'>, value: string | boolean) => {
    // Prevent country field changes - always keep it as Philippines
    if (field === 'country') {
      return;
    }
    
    // Apply character filtering for specific fields
    let processedValue = value;
    if (typeof value === 'string') {
      if (field === 'firstName' || field === 'middleName' || field === 'lastName') {
        processedValue = filterNameField(value);
      } else if (field === 'houseUnitNo' || field === 'streetName' || field === 'subdivisionVillage') {
        processedValue = filterAddressField(value);
      } else if (field === 'idNumber') {
        processedValue = filterIdNumberField(value);
      }
    }
    
    setFormData(prev => {
      const newData = { ...prev, [field]: processedValue };
      
      // NEW: Clear ID type and ID number when nationality changes (same logic as PrincipalInsuredStep)
      if (field === 'nationality' && typeof processedValue === 'string' && processedValue !== prev.nationality) {
        newData.idType = '';
        newData.idNumber = '';
      }
      
      // Clear city when province changes
      if (field === 'province' && typeof processedValue === 'string' && processedValue !== prev.province) {
        newData.city = '';
        newData.barangay = ''; // Also clear barangay when province changes
        newData.zipCode = ''; // Clear zip code when province changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Clear barangay when city changes
      if (field === 'city' && typeof processedValue === 'string' && processedValue !== prev.city) {
        newData.barangay = '';
        newData.zipCode = ''; // Clear zip code when city changes
        setIsZipCodeAutoPopulated(false); // Reset auto-population flag
      }
      
      // Auto-populate zip code when barangay is selected
      if (field === 'barangay' && typeof processedValue === 'string') {
        if (processedValue && shouldAutoPopulateZipCode(processedValue, barangays)) {
          const zipCode = getZipCodeFromBarangay(barangays, processedValue);
          if (zipCode) {
            newData.zipCode = zipCode;
            setIsZipCodeAutoPopulated(true);
          } else {
            // If no zip code found, allow manual entry
            setIsZipCodeAutoPopulated(false);
          }
        } else {
          // If barangay is cleared, reset zip code and auto-population flag
          newData.zipCode = '';
          setIsZipCodeAutoPopulated(false);
        }
      }
      
      return newData;
    });

    // Clear field error when user starts typing (new approach)
    clearFieldError(field);
    
    // Clear error when user starts typing (existing approach)
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        
        // Also clear ID type and ID number errors when nationality changes
        if (field === 'nationality') {
          delete newErrors.idType;
          delete newErrors.idNumber;
        }
        
        // Also clear city and barangay errors when province changes
        if (field === 'province') {
          delete newErrors.city;
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Also clear barangay error when city changes
        if (field === 'city') {
          delete newErrors.barangay;
          delete newErrors.zipCode;
        }
        
        // Clear zip code error when barangay changes
        if (field === 'barangay') {
          delete newErrors.zipCode;
        }
        
        return newErrors;
      });
    }
  };

  // Helper functions for field validation (like PrincipalInsuredStep)
  const getValidationErrors = (): Record<string, boolean> => {
    const errors: Record<string, boolean> = {};
    
    // Required fields validation
    if (!formData.title?.trim()) errors.title = true;
    if (!formData.firstName?.trim()) errors.firstName = true;
    if (!formData.lastName?.trim()) errors.lastName = true;
    if (!formData.dateOfBirth?.trim()) errors.dateOfBirth = true;
    if (!formData.gender || formData.gender === 'Choose Gender') errors.gender = true;
    if (!formData.nationality || formData.nationality === 'Nationality') errors.nationality = true; // NEW: Nationality validation
    if (!formData.houseUnitNo?.trim()) errors.houseUnitNo = true;
    if (!formData.barangay?.trim()) errors.barangay = true;
    if (!formData.city?.trim()) errors.city = true;
    if (!formData.province?.trim()) errors.province = true;
    if (!formData.zipCode?.trim()) errors.zipCode = true;
    if (!formData.idType || formData.idType === 'Drivers License') errors.idType = true;
    if (!formData.idNumber?.trim()) errors.idNumber = true;
    
    return errors;
  };

  // Helper function to get field error class
  const getFieldErrorClass = (fieldName: string): string => {
    return showValidationErrors && fieldErrors[fieldName] ? 'field-error' : '';
  };

  // Helper function to clear field error when user starts typing
  const clearFieldError = (fieldName: string) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation (address fields commented out)
    const requiredFields = [
      "title",
      "firstName",
      "lastName",
      "dateOfBirth",
      "gender",
      "nationality",
      "houseUnitNo",

      "city",
      "province",
      "zipCode",
      "idType",
      "idNumber",
    ];

    requiredFields.forEach(field => {
      const value = formData[field as keyof typeof formData];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors[field] = `${field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} is required`;
      }
    });

    // Enhanced validation with character limits and patterns
    if (formData.firstName && !validateNameField(formData.firstName)) {
      newErrors.firstName = 'First name contains invalid characters';
    }
    if (formData.middleName && !validateNameField(formData.middleName)) {
      newErrors.middleName = 'Middle name contains invalid characters';
    }
    if (formData.lastName && !validateNameField(formData.lastName)) {
      newErrors.lastName = 'Last name contains invalid characters';
    }
    if (formData.houseUnitNo && !validateAddressField(formData.houseUnitNo)) {
      newErrors.houseUnitNo = 'House/Unit No. contains invalid characters';
    }
    if (formData.streetName && !validateAddressField(formData.streetName)) {
      newErrors.streetName = 'Street name contains invalid characters';
    }
    if (formData.subdivisionVillage && !validateAddressField(formData.subdivisionVillage)) {
      newErrors.subdivisionVillage = 'Subdivision/Village contains invalid characters';
    }
    if (formData.idNumber && !validateIdNumberField(formData.idNumber)) {
      newErrors.idNumber = 'ID number contains invalid characters';
    }

    // Gender validation
    if (formData.gender === '' || formData.gender === 'Choose Gender') {
      newErrors.gender = 'Please select a gender';
    }

    // ID Type validation
    if (formData.idType === '' || formData.idType === 'Drivers License') {
      newErrors.idType = 'Please select an ID type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    // Check for validation errors (new approach)
    const validationErrors = getValidationErrors();
    const hasErrors = Object.keys(validationErrors).length > 0;
    
    if (hasErrors) {
      // Show validation errors by highlighting fields
      setFieldErrors(validationErrors);
      setShowValidationErrors(true);
      return; // Don't proceed if there are validation errors
    }
    
    // Clear any previous validation errors
    setFieldErrors({});
    setShowValidationErrors(false);
    
    // Existing validation
    if (!validateForm()) {
      return;
    }

    const spouseData: SpouseInformation = {
      id: spouse?.id || Date.now().toString(),
      ...formData
    };

    onSave(spouseData);
    onClose();
  };

  const handleDelete = () => {
    if (spouse && onDelete) {
      onDelete(spouse.id);
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="spouse-modal-backdrop" onClick={handleBackdropClick}>
      <div className="spouse-modal-container">
        {/* Modal Content */}
        <div className="spouse-modal-content">
          <div className="spouse-modal-card">
            {/* All Content - Header, Form, and Footer are now scrollable */}
            <div className="spouse-modal-form-content">
              {/* Modal Header - Now inside scrollable area */}
              <div className="spouse-modal-form-header">
                <div className="spouse-modal-title-section">
                  <h2 className="spouse-modal-form-title">Spouse Information</h2>
                  <p className="spouse-modal-form-subtitle">
                    You have option to fill or not to fill this information
                  </p>
                </div>
                <button
                  className="spouse-modal-close"
                  onClick={onClose}
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>
              <div className="spouse-form">
                {/* Name Fields Row */}
                <div className="form-row name-row">
                  <div
                    className={`form-group title-group ${getFieldErrorClass("title")}`}
                  >
                    <label className="form-label">Title *</label>
                    <select
                      className={`form-select ${errors.title ? 'error' : ''}`}
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                    >
                      <option value="">Title</option>
                      <option value="Ms.">Ms.</option>
                      <option value="Mrs.">Mrs.</option>
                      <option value="Mr.">Mr.</option>
                    </select>
                    {errors.title && <span className="error-message">{errors.title}</span>}
                  </div>

                  <div className={`form-group ${getFieldErrorClass('firstName')}`}>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.firstName ? 'error' : ''}`}
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Middle Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.middleName}
                      onChange={(e) => handleInputChange('middleName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('lastName')}`}>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.lastName ? 'error' : ''}`}
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      placeholder=""
                      maxLength={NAME_FIELD_MAX_LENGTH}
                    />
                  </div>

                  <div className="form-group suffix-group">
                    <label className="form-label">Suffix</label>
                    <select
                      className="form-select"
                      value={formData.suffix}
                      onChange={(e) => handleInputChange('suffix', e.target.value)}
                    >
                      <option value="N/A">N/A</option>
                      <option value="Jr.">Jr.</option>
                      <option value="Sr.">Sr.</option>
                      <option value="II">II</option>
                      <option value="III">III</option>
                    </select>
                  </div>
                </div>

                {/* Date of Birth, Age, Gender Row */}
                <div className="form-row">
                  <div className={`form-group ${getFieldErrorClass('dateOfBirth')}`}>
                    <label className="form-label">Date of Birth *</label>
                    {/* Updated: Fixed 1950 birth year validation for Family Plans */}
                    <DatePicker
                      value={formData.dateOfBirth}
                      onChange={(value) => handleInputChange('dateOfBirth', value)}
                      placeholder="MM/DD/YYYY"
                      name="spouse-dateOfBirth"
                      className={`spouse-datepicker form-datepicker ${errors.dateOfBirth ? 'error' : ''}`}
                      position="above"
                      validationRules={dateUtils.createFamilyPlanBirthdateRules()}
                      error={errors.dateOfBirth}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Age *</label>
                    <input
                      type="text"
                      className="form-input spouse-info-age-read-only"
                      value={formData.age}
                      readOnly
                      placeholder="Auto-calculated"
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('gender')}`}>
                    <label className="form-label">Gender *</label>
                    <select
                      className={`form-select ${errors.gender ? 'error' : ''}`}
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                      <option value="">Choose Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>

                  <div className={`form-group ${getFieldErrorClass('nationality')}`}>
                    <label className="form-label">Nationality *</label>
                    <SearchableDropdown
                      options={nationalitiesOptions}
                      selectedValue={formData.nationality}
                      onChange={(value) => handleInputChange('nationality', value)}
                      placeholder={nationalitiesLoading ? "Loading nationalities..." : "Choose Nationality"}
                      searchPlaceholder="Search nationalities..."
                      disabled={nationalitiesLoading || !!nationalitiesError}
                      dropdownId="spouse-nationality-dropdown"
                      className={errors.nationality ? 'error' : ''}
                      error={!!errors.nationality}
                      position="auto"
                    />
                  </div>
                </div>

                {/* Address Sharing Checkboxes */}
                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.shareAddressWithPrincipal || false}
                        onChange={(e) => handleInputChange('shareAddressWithPrincipal', e.target.checked)}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Do you and your spouse share the same home address?</span>
                    </label>
                  </div>
                </div>
                

                {/* Address Fields */}
                <div className="form-row spouse-modal-address-first-line">
                  <div className={`form-group ${getFieldErrorClass('houseUnitNo')}`}>
                    <label className="form-label">House/Unit No., Street Name, Subdivision/Village *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.houseUnitNo ? 'error' : ''}`}
                      value={formData.houseUnitNo}
                      onChange={(e) => handleInputChange('houseUnitNo', e.target.value)}
                      placeholder=""
                      maxLength={ADDRESS_FIELD_MAX_LENGTH}
                    />
                  </div>
                  <div className={`form-group ${getFieldErrorClass('province')}`}>
                    <label className="form-label">Province *</label>
                    <SearchableDropdown
                      options={provinceOptions}
                      selectedValue={formData.province}
                      onChange={(value) => handleInputChange('province', value)}
                      placeholder={provincesLoading ? "Loading provinces..." : "Select Province"}
                      searchPlaceholder="Search provinces..."
                      className={errors.province ? 'error' : ''}
                      error={!!errors.province}
                      position="auto"
                      dropdownId="spouse-province"
                      disabled={provincesLoading || !!provincesError}
                    />
                  </div>
                </div>
                <div className="form-row spouse-modal-address-second-line">
                  <div className={`form-group ${getFieldErrorClass('city')}`}>
                    <label className="form-label">City *</label>
                    <SearchableDropdown
                      options={cityOptions}
                      selectedValue={formData.city}
                      onChange={(value) => handleInputChange('city', value)}
                      placeholder={
                        !formData.province
                          ? "Select City"
                          : citiesLoading
                            ? "Loading cities..."
                            : "Select City"
                      }
                      searchPlaceholder="Search cities..."
                      className={errors.city ? 'error' : ''}
                      error={!!errors.city}
                      position="auto"
                      dropdownId="spouse-city"
                      disabled={!formData.province || citiesLoading || !!citiesError}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('barangay')}`}>
                    <label className="form-label">Barangay *</label>
                    <SearchableDropdown
                      options={barangayOptions}
                      selectedValue={formData.barangay}
                      onChange={(value) => handleInputChange('barangay', value)}
                      placeholder={
                        !formData.city
                          ? "Select Barangay"
                          : barangaysLoading
                            ? "Loading barangays..."
                            : "Select Barangay"
                      }
                      searchPlaceholder="Search barangays..."
                      className={errors.barangay ? 'error' : ''}
                      error={!!errors.barangay}
                      position="auto"
                      dropdownId="spouse-barangay"
                      disabled={!formData.city || barangaysLoading || !!barangaysError}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('zipCode')}`}>
                    <label className="form-label">Zip Code *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.zipCode ? 'error' : ''} ${isZipCodeAutoPopulated ? 'auto-populated' : ''}`}
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      placeholder={isZipCodeAutoPopulated ? "Auto-populated from barangay" : "Zip Code"}
                      maxLength={4}
                      readOnly={isZipCodeAutoPopulated}
                    />
                  </div>

                  <div className={`form-group ${getFieldErrorClass('country')}`}>
                    <label className="form-label">Country *</label>
                    <SearchableDropdown
                      options={['Philippines']}
                      selectedValue="Philippines"
                      onChange={() => {}} // No-op function since it's disabled
                      placeholder="Philippines"
                      searchPlaceholder="Philippines"
                      className={`${errors.country ? 'error' : ''} disabled-country-field`}
                      error={!!errors.country}
                      position="auto"
                      dropdownId="spouse-country"
                      disabled={true} // Always disabled
                    />
                  </div>
                </div>

                {/* ID Type and Number */}
                <div className="form-row spouse-modal-address-third-line">
                  <div className={`form-group ${getFieldErrorClass('idType')}`}>
                    <label className="form-label">ID Type *</label>
                    <select
                      className={`form-select ${errors.idType ? 'error' : ''}`}
                      value={formData.idType}
                      onChange={(e) => handleInputChange('idType', e.target.value)}
                      disabled={idTypesLoading}
                    >
                      <option value="">
                        {idTypesLoading ? 'Loading ID Types...' : 'Choose ID Type'}
                      </option>
                      {idTypesError && (
                        <option value="" disabled>
                          Error loading ID types
                        </option>
                      )}
                      {idTypesOptions
                        .filter((idType) => {
                          return formData.nationality === "Filipino" || formData.nationality === ""
                            ? idType.isNonFilipino === false
                            : idType.isNonFilipino === true;
                        })
                        .map((idType) => (
                          <option key={idType.name} value={idType.name}>
                            {idType.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className={`form-group ${getFieldErrorClass('idNumber')}`}>
                    <label className="form-label">ID Number *</label>
                    <input
                      type="text"
                      className={`form-input ${errors.idNumber ? 'error' : ''}`}
                      value={formData.idNumber}
                      onChange={(e) => handleInputChange('idNumber', e.target.value)}
                      placeholder=""
                      maxLength={ID_NUMBER_MAX_LENGTH}
                    />
                  </div>
                </div>

                {/* Error messages */}
                {nationalitiesError && (
                  <div className="error-message">
                    <p>Unable to load nationalities. Please refresh the page or try again later.</p>
                  </div>
                )}
                {idTypesError && (
                  <div className="error-message">
                    <p>Unable to load ID types. Please refresh the page or try again later.</p>
                  </div>
                )}
              </div>

              {/* Modal Footer - Now inside scrollable content */}
              <div className="spouse-modal-footer">
                {isEditMode && onDelete ? (
                  <div className="spouse-modal-footer-left">
                    <button
                      className="spouse-modal-delete-btn"
                      onClick={handleDelete}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="delete-icon"
                      >
                        <path
                          d="M3 6H5H21"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10 11V17"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 11V17"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Delete Family Member
                    </button>
                    <div className='spouse-modal-delete-warning-position'>
                      <p className="spouse-modal-delete-warning">
                        By clicking this button it will permanently remove this as family member.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div></div>
                )}

                <div className="spouse-modal-footer-actions">
                  <button
                    className="spouse-modal-btn-cancel"
                    onClick={onClose}
                  >
                    CANCEL
                  </button>
                  <button
                    className={`spouse-modal-btn-save ${isEditMode && !hasFormChanges ? 'disabled' : ''}`}
                    onClick={handleSave}
                    disabled={isEditMode && !hasFormChanges}
                  >
                    SAVE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SpouseInformationModal;
